import React, { useState, useEffect } from 'react';
import { Form, Button, Icon } from 'semantic-ui-react';
import { updateProjectBid } from '~/api/projectBids';
import toast from 'react-hot-toast';

interface BidIrrFormProps {
    bid: {
        id: string;
        calculatedIrr?: number;
        manualIrr?: number;
        irrSpreadsheetUrl?: string;
        manualIrrSpreadsheetUrl?: string | null;
    };
    projectId: string;
    onSave: (updatedBid: any) => void;
    onCancel: () => void;
}

const BidIrrForm: React.FC<BidIrrFormProps> = ({ bid, projectId, onSave, onCancel }) => {
    const [manualIrr, setManualIrr] = useState<string>(
        bid.manualIrr !== null && bid.manualIrr !== undefined
            ? (bid.manualIrr * 100).toString()
            : ''
    );
    const [manualIrrSpreadsheetUrl, setManualIrrSpreadsheetUrl] = useState<string>(
        bid.manualIrrSpreadsheetUrl ?? ''
    );
    const [loading, setLoading] = useState(false);

    // Sync form state when bid changes (e.g. opening modal for a different bid or after save)
    useEffect(() => {
        setManualIrr(
            bid.manualIrr !== null && bid.manualIrr !== undefined
                ? (bid.manualIrr * 100).toString()
                : ''
        );
        setManualIrrSpreadsheetUrl(bid.manualIrrSpreadsheetUrl ?? '');
    }, [bid.id, bid.manualIrr, bid.manualIrrSpreadsheetUrl]);

    const formatIrrDisplay = (irr?: number) => {
        if (irr == null) return 'Not calculated';
        return `${(irr * 100).toFixed(2)}%`;
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const manualIrrValue = manualIrr === '' ? null : parseFloat(manualIrr) / 100;
            const manualIrrSpreadsheetUrlValue = manualIrrSpreadsheetUrl === '' ? null : manualIrrSpreadsheetUrl;

            const updatedBid = await updateProjectBid(bid.id, projectId, {
                manualIrr: manualIrrValue,
                manualIrrSpreadsheetUrl: manualIrrSpreadsheetUrlValue
            });

            toast.success('IRR updated successfully');
            onSave(updatedBid);
        } catch (error: any) {
            console.error('Failed to update IRR:', error);
            toast.error(error?.response?.data?.message || error?.message || 'Failed to update IRR');
        } finally {
            setLoading(false);
        }
    };

    const handleTriggerRecalculation = async () => {
        setLoading(true);
        try {
            const updatedBid = await updateProjectBid(bid.id, projectId, {
                triggerIrrRecalculation: true
            });

            toast.success('IRR recalculation triggered successfully');
            onSave(updatedBid);
        } catch (error: any) {
            console.error('Failed to trigger recalculation:', error);
            toast.error(error?.response?.data?.message || error?.message || 'Failed to trigger recalculation');
        } finally {
            setLoading(false);
        }
    }; 
    
    const handleManualIrrChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Allow empty string, numbers, and decimals
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setManualIrr(value);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            onCancel();
        }
    };

    return (
        <div className="bid-irr-form-container">
            <Form>

                <div className="bid-irr-form-row">
                    <label className="bid-irr-form-label">Manual IRR (%)</label>
                    <input
                        className="bid-irr-input"
                        type="text"
                        value={manualIrr}
                        onChange={handleManualIrrChange}
                        onKeyDown={handleKeyPress}
                        placeholder="Enter percentage (e.g., 8.5)"
                        disabled={loading}
                        autoFocus
                    />
                </div>

                <div className="bid-irr-form-row">
                    <label className="bid-irr-form-label">Manual IRR Spreadsheet URL</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
                        <input
                            className="bid-irr-input"
                            type="text"
                            value={manualIrrSpreadsheetUrl}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setManualIrrSpreadsheetUrl(e.target.value)
                            }
                            onKeyDown={handleKeyPress}
                            placeholder="https://docs.google.com/..."
                            disabled={loading}
                            style={{ flex: 1 }}
                        />
                        <Button
                            size="small"
                            type="button"
                            disabled={loading || manualIrrSpreadsheetUrl === ''}
                            onClick={() => setManualIrrSpreadsheetUrl('')}
                        >
                            Clear
                        </Button>
                    </div>
                </div>

                <div className="bid-irr-form-row">
                    <label className="bid-irr-form-label">Calculated IRR</label>
                    <div className="bid-irr-readonly-value">
                        {formatIrrDisplay(bid.calculatedIrr)}
                        <Button
                            secondary
                            size="small"
                            onClick={handleTriggerRecalculation}
                            style={{ cursor: 'pointer' }}
                        >
                            {loading ? 'Calculating...' : 'Calculate'}
                        </Button>
                    </div>
                </div>

                {process.env.REACT_APP_ENV !== 'QA2' && (
                    <>
                        {bid.manualIrrSpreadsheetUrl && (
                            <div className="bid-irr-form-row">
                                <a
                                    className="bid-irr-spreadsheet-link"
                                    href={bid.manualIrrSpreadsheetUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: 'flex', alignItems: 'center' }}
                                >
                                    View Manual IRR Spreadsheet
                                    <Icon name="external" style={{ marginTop: '-6px' }} />
                                </a>
                            </div>
                        )}
                        {bid.irrSpreadsheetUrl && (
                            <div className="bid-irr-form-row">
                                <a
                                    className="bid-irr-spreadsheet-link"
                                    href={bid.irrSpreadsheetUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: 'flex', alignItems: 'center' }}
                                >
                                    View Calculated IRR Spreadsheet
                                    <Icon name="external" style={{ marginTop: '-6px' }} />
                                </a>
                            </div>
                        )}
                    </>
                )}



                <div className="bid-irr-button-group">
                    <Button
                        size="small"
                        onClick={onCancel}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        primary
                        size="small"
                        onClick={handleSave}
                        loading={loading}
                        disabled={loading}
                    >
                        Save
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default BidIrrForm;
